import { ReviewItem } from '../types';

type QuestionField = {
  id: string;
  type: string;
  label: string;
  createdAt?: string;
  deprecated?: boolean;
  deprecatedAt?: string;
  historicallyVisible?: boolean;
  [key: string]: any;
};

type QuestionsSchema = {
  fields: QuestionField[];
  [key: string]: any;
};

type CategoryNode = {
  id: number;
  name: string;
  items?: ReviewItem[];
  subcategories?: CategoryNode[];
  [key: string]: any;
};

export function filterQuestionsForReview(
  questionsSchema: QuestionsSchema,
  reviewCreatedAt: Date
): QuestionsSchema {
  if (!questionsSchema || !Array.isArray(questionsSchema.fields)) {
    return { ...questionsSchema, fields: [] };
  }

  const reviewTime = reviewCreatedAt.getTime();

  const filteredFields = questionsSchema.fields
    .filter(field => {
      if (field.createdAt) {
        const createdTime = new Date(field.createdAt).getTime();
        if (createdTime > reviewTime) {
          return false;
        }
      }

      if (field.deprecated) {
        if (!field.deprecatedAt) {
          return false;
        }

        const deprecatedTime = new Date(field.deprecatedAt).getTime();
        if (deprecatedTime <= reviewTime) {
          return false;
        }
      }

      return true;
    })
    .map(field => {
      if (field.deprecated && field.deprecatedAt) {
        const deprecatedTime = new Date(field.deprecatedAt).getTime();
        if (deprecatedTime > reviewTime) {
          return {
            ...field,
            historicallyVisible: true,
          };
        }
      }
      return field;
    });

  return {
    ...questionsSchema,
    fields: filteredFields,
  };
}

export function filterItemsForReview(items: ReviewItem[] = [], reviewCreatedAt: Date): ReviewItem[] {
  if (!Array.isArray(items)) {
    return [];
  }

  const reviewTime = reviewCreatedAt.getTime();

  return items.filter(item => {
    if (item.createdAt) {
      const createdTime = new Date(item.createdAt as any).getTime();
      if (createdTime > reviewTime) {
        return false;
      }
    }

    if ((item as any).isDeprecated) {
      if (!(item as any).deprecatedAt) {
        return false;
      }

      const deprecatedTime = new Date((item as any).deprecatedAt).getTime();
      if (deprecatedTime <= reviewTime) {
        return false;
      }
    }

    return true;
  });
}

export function filterCategoriesForReview(
  categories: CategoryNode[] = [],
  reviewCreatedAt: Date
): CategoryNode[] {
  if (!Array.isArray(categories)) {
    return [];
  }

  return categories.map(category => {
    const filteredCategory: CategoryNode = { ...category };

    if (filteredCategory.items && Array.isArray(filteredCategory.items)) {
      const itemsExistingAtReviewTime = filterItemsForReview(filteredCategory.items, reviewCreatedAt);

      filteredCategory.items = itemsExistingAtReviewTime.map(item => {
        if ((item as any).questionsSchema) {
          return {
            ...item,
            questionsSchema: filterQuestionsForReview((item as any).questionsSchema, reviewCreatedAt),
          };
        }
        return item;
      });
    }

    if (filteredCategory.subcategories && Array.isArray(filteredCategory.subcategories)) {
      filteredCategory.subcategories = filterCategoriesForReview(
        filteredCategory.subcategories,
        reviewCreatedAt
      );
    }

    return filteredCategory;
  });
}

export function pruneEmptyCategories(categories: CategoryNode[] = []): CategoryNode[] {
  if (!Array.isArray(categories)) {
    return [];
  }

  return categories
    .map(category => {
      const nextCategory = { ...category };
      if (nextCategory.subcategories) {
        nextCategory.subcategories = pruneEmptyCategories(nextCategory.subcategories);
      }
      return nextCategory;
    })
    .filter(category => {
      const hasItems = Array.isArray(category.items) && category.items.length > 0;
      const hasSubcategories =
        Array.isArray(category.subcategories) && category.subcategories.length > 0;
      return hasItems || hasSubcategories;
    });
}

export function mergeDeprecatedItems(categories: CategoryNode[] = [], responses: any[] = []): CategoryNode[] {
  if (!Array.isArray(categories) || !Array.isArray(responses) || responses.length === 0) {
    return categories;
  }

  const deprecatedItemsMap = new Map<number, any>();
  const itemCategoryMap = new Map<number, number>();

  responses.forEach(response => {
    const item = response?.item;
    if (item && item.id && item.isDeprecated && !deprecatedItemsMap.has(item.id)) {
      const serialized = item.toJSON ? item.toJSON() : item;
      deprecatedItemsMap.set(item.id, serialized);
      if (serialized.categoryId) {
        itemCategoryMap.set(item.id, serialized.categoryId);
      }
    }
  });

  if (deprecatedItemsMap.size === 0) {
    return categories;
  }

  const categoryMap = new Map<number, any>();

  const indexCategories = (cats: any[]) => {
    if (!Array.isArray(cats)) return;
    cats.forEach(cat => {
      const data = cat.toJSON ? cat.toJSON() : cat;
      categoryMap.set(data.id, data);
      if (Array.isArray(data.subcategories)) {
        indexCategories(data.subcategories);
      }
    });
  };

  indexCategories(categories);

  deprecatedItemsMap.forEach((item, itemId) => {
    const categoryId = itemCategoryMap.get(itemId);
    if (!categoryId) {
      return;
    }

    const category = categoryMap.get(categoryId);
    if (!category) {
      return;
    }

    if (!Array.isArray(category.items)) {
      category.items = [];
    }

    const exists = category.items.some((existing: any) => existing.id === item.id);
    if (exists) {
      return;
    }

    category.items.push(item);
  });

  const sortItems = (cats: any[]) => {
    if (!Array.isArray(cats)) {
      return;
    }

    cats.forEach(cat => {
      if (Array.isArray(cat.items) && cat.items.length > 0) {
        cat.items.sort((a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0));
      }
      if (Array.isArray(cat.subcategories)) {
        sortItems(cat.subcategories);
      }
    });
  };

  sortItems(categories);

  return categories;
}
